/**
 * Global state management for UI concerns using signals
 *
 * This store centralizes UI state that was previously managed with React hooks.
 * It handles scroll management, panel sizes, and other UI-related state.
 *
 * Key features:
 * 1. Scroll position management
 * 2. Panel resize state
 * 3. Modal visibility states
 * 4. UI preferences
 * 5. Message list scroll behavior
 */

import type { Theme } from '@elara/client-core/ui-contracts/theme';

import { createState } from '@elara/client-core/lib/signals-utils';

// Theme is a platform-neutral preference shared with the data layer via @elara/client-core
// (ELARA-700); re-exported so existing #states/UIState consumers are unchanged.
export type { Theme } from '@elara/client-core/ui-contracts/theme';

export function createUiState() {
  const state = createState({
    /** Whether the sidebar is open */
    isSidebarOpen: false,

    /** Whether the debug panel is visible */
    isDebugPanelVisible: false,

    /** Toggles the debug panel */
    toggleDebugPanel(visible?: boolean) {
      this.isDebugPanelVisible = visible ?? !this.isDebugPanelVisible;
    },

    /** Toggles the sidebar */
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },

    /** Applies the theme to the page */
    applyTheme(theme: Theme) {
      if (theme === 'system') {
        const prefersDark = globalThis.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.classList.toggle('dark', prefersDark);
      } else {
        document.documentElement.classList.toggle('dark', theme === 'dark');
      }
    },
  });
  return state;
}

export type UIState = ReturnType<typeof createUiState>;
